/* Servicio de push notifications con Firebase Cloud Messaging.
   - Web / PWA: usa firebase/messaging + service worker en /firebase-messaging-sw.js
   - Nativo (Capacitor): usa @capacitor/push-notifications (fallback dinámico)
   - API pública:
       await registerPushIfPossible()  → registra token en el backend si tiene permiso
       await requestPushPermission()   → pide permiso y registra (para el botón "Activar")
       onPushMessage(cb)               → suscribe a foreground messages
*/
import api from "@/lib/api";
import { getFirebase, isFirebaseConfigured, VAPID_KEY } from "@/lib/firebase";

const LS_TOKEN = "sns_push_token";

async function getCap() {
  try {
    const { Capacitor } = await import("@capacitor/core");
    return Capacitor?.isNativePlatform?.() === true ? Capacitor : null;
  } catch {
    return null;
  }
}

async function isNative() {
  return !!(await getCap());
}

const emitNavigate = (link) => {
  if (link) window.dispatchEvent(new CustomEvent("sns:navigate", { detail: link }));
};

/* ---------- WEB / PWA ---------- */
async function registerWebPush(silent = false) {
  if (!isFirebaseConfigured()) return { ok: false, reason: "not_configured" };
  if (typeof Notification === "undefined") return { ok: false, reason: "unsupported" };

  // Estado del permiso
  let permission = Notification.permission;
  if (permission === "default") {
    if (silent) return { ok: false, reason: "no_permission" };
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") return { ok: false, reason: "denied" };

  // Registrar service worker
  if (!("serviceWorker" in navigator)) return { ok: false, reason: "no_sw" };
  const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");

  // getToken
  const { getMessaging, getToken, onMessage } = await import("firebase/messaging");
  const app = getFirebase();
  const messaging = getMessaging(app);

  const token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: reg,
  });
  if (!token) return { ok: false, reason: "no_token" };

  // Enviar al backend (evita duplicados con localStorage)
  const previous = localStorage.getItem(LS_TOKEN);
  if (previous !== token) {
    try {
      await api.post("/push/register", { token, platform: "web" });
      localStorage.setItem(LS_TOKEN, token);
    } catch (ex) {
      console.warn("[push] register failed:", ex);
      return { ok: false, reason: "backend_error" };
    }
  }

  // Foreground listener: mostrar toast simple
  onMessage(messaging, async (payload) => {
    const title = payload?.notification?.title || "Notificación";
    const body = payload?.notification?.body || "";
    console.log("[push] foreground message received:", title, body);
    let shown = false;
    try {
      // Necesitamos una registration ACTIVA — refrescamos por si el SW se recicló
      const activeReg = (await navigator.serviceWorker?.getRegistration("/firebase-messaging-sw.js")) || reg;
      if (activeReg && Notification.permission === "granted") {
        await activeReg.showNotification(title, {
          body,
          icon: "/images/logo-brand.png",
          badge: "/images/logo-brand.png",
          tag: "sns-push-" + Date.now(),
          requireInteraction: false,
          data: payload?.data || {},
        });
        shown = true;
      }
    } catch (e) {
      console.warn("[push] showNotification failed:", e);
    }
    // Fallback: siempre disparar evento custom para que la app muestre toast
    try {
      window.dispatchEvent(new CustomEvent("sns:push", {
        detail: { title, body, native_shown: shown, payload }
      }));
    } catch {}
  });

  return { ok: true, token };
}

/* ---------- NATIVO (Capacitor) ---------- */
let nativeListenersReady = false;

async function sendNativeToken(Cap, token) {
  await api.post("/push/register", { token, platform: Cap.getPlatform() === "ios" ? "ios" : "android" });
  localStorage.setItem(LS_TOKEN, token);
}

async function registerViaFirebaseMessaging(Cap, silent) {
  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  let status = (await FirebaseMessaging.checkPermissions()).receive;
  if (status !== "granted") {
    if (silent) return { ok: false, reason: "no_permission" };
    status = (await FirebaseMessaging.requestPermissions()).receive;
  }
  if (status !== "granted") return { ok: false, reason: "denied" };
  if (!nativeListenersReady) {
    nativeListenersReady = true;
    FirebaseMessaging.addListener("notificationActionPerformed", (e) => emitNavigate(e?.notification?.data?.link));
    FirebaseMessaging.addListener("notificationReceived", (e) => {
      const n = e?.notification || {};
      window.dispatchEvent(new CustomEvent("sns:push", { detail: { title: n.title, body: n.body, payload: n } }));
    });
    FirebaseMessaging.addListener("tokenReceived", (e) => { if (e?.token) sendNativeToken(Cap, e.token).catch(() => {}); });
  }
  const { token } = await FirebaseMessaging.getToken();
  if (!token) return { ok: false, reason: "no_token" };
  try { await sendNativeToken(Cap, token); }
  catch (ex) { return { ok: false, reason: "backend_error", error: String(ex) }; }
  return { ok: true, token };
}

// Fallback Android: plugin oficial PushNotifications (su token ya es FCM en Android)
async function registerViaPushNotifications(Cap, silent) {
  const { registerPlugin } = await import("@capacitor/core");
  const PushNotifications = registerPlugin("PushNotifications");
  let status = (await PushNotifications.checkPermissions()).receive;
  if (status !== "granted") {
    if (silent) return { ok: false, reason: "no_permission" };
    status = (await PushNotifications.requestPermissions()).receive;
  }
  if (status !== "granted") return { ok: false, reason: "denied" };
  if (!nativeListenersReady) {
    nativeListenersReady = true;
    PushNotifications.addListener("pushNotificationActionPerformed", (e) => emitNavigate(e?.notification?.data?.link));
  }
  return await new Promise((resolve) => {
    const to = setTimeout(() => resolve({ ok: false, reason: "no_token_timeout" }), 15000);
    PushNotifications.addListener("registration", async (t) => {
      clearTimeout(to);
      try { await sendNativeToken(Cap, t.value); resolve({ ok: true, token: t.value }); }
      catch (ex) { resolve({ ok: false, reason: "backend_error", error: String(ex) }); }
    });
    PushNotifications.addListener("registrationError", (err) => {
      clearTimeout(to);
      resolve({ ok: false, reason: "native_error", error: String(err?.error || err) });
    });
    PushNotifications.register();
  });
}

async function registerNativePush(silent = false) {
  const Cap = await getCap();
  try {
    if (Cap.isPluginAvailable("FirebaseMessaging")) return await registerViaFirebaseMessaging(Cap, silent);
    if (Cap.getPlatform() === "android" && Cap.isPluginAvailable("PushNotifications")) {
      return await registerViaPushNotifications(Cap, silent);
    }
    return { ok: false, reason: "app_outdated" };
  } catch (ex) {
    return { ok: false, reason: "native_error", error: String(ex?.message || ex) };
  }
}

/* ---------- API pública ---------- */
export async function registerPushIfPossible() {
  // Llamado tras login. Silencioso: no pide permisos si son "default".
  if (await isNative()) return registerNativePush(true);
  return registerWebPush(true);
}

export async function requestPushPermission() {
  // Llamado desde botón explícito. Pide permisos si son "default".
  if (await isNative()) return registerNativePush(false);
  return registerWebPush(false);
}

export function currentPushToken() {
  return localStorage.getItem(LS_TOKEN);
}

export async function unregisterCurrentToken() {
  const token = currentPushToken();
  if (!token) return;
  try { await api.post("/push/unregister", { token, platform: "web" }); } catch {}
  localStorage.removeItem(LS_TOKEN);
}
